import { AnimatePresence, motion } from "framer-motion";
import { type FC, useEffect, useRef, useState } from "react";

import { getColorClassForEvent } from "./eventColors";
import { EventIcon } from "./EventIcon";

export interface EventTypeFilterOption {
  count: number;
  eventType: string;
}

interface ProviderProgressEventTypeFilterProps {
  options: EventTypeFilterOption[];
  selectedEventTypes: ReadonlySet<string>;
  onIsolate: (eventType: string) => void;
  onReset: () => void;
  onToggle: (eventType: string) => void;
}

const FilterIcon: FC = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M4 6h16M7 12h10m-7 6h4"
    />
  </svg>
);

const ChevronIcon: FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M6 9l6 6 6-6"
    />
  </svg>
);

function formatEventTypeLabel(eventType: string): string {
  return eventType.replace(":", "::");
}

const EventTypeFilterChip: FC<{
  onIsolate: () => void;
  isSelected: boolean;
  onToggle: () => void;
  option: EventTypeFilterOption;
}> = ({ isSelected, onIsolate, onToggle, option }) => {
  const colorClass = getColorClassForEvent(option.eventType);
  const clickTimeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (clickTimeoutRef.current !== null) {
        window.clearTimeout(clickTimeoutRef.current);
      }
    },
    [],
  );

  const handlePress = () => {
    if (clickTimeoutRef.current !== null) {
      window.clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
      onIsolate();
      return;
    }

    clickTimeoutRef.current = window.setTimeout(() => {
      clickTimeoutRef.current = null;
      onToggle();
    }, 220);
  };

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={handlePress}
      className={`inline-flex min-h-10 w-full items-center justify-between gap-2 rounded-none border px-2.5 py-1.5 text-left transition-all duration-200 sm:min-h-9 sm:w-auto sm:justify-start ${
        isSelected
          ? "border-emerald-400 bg-emerald-50 text-slate-900 shadow-sm dark:border-emerald-500/60 dark:bg-emerald-500/10 dark:text-slate-100"
          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-800"
      }`}
      title={`Toggle ${option.eventType}. Double-press to isolate.`}
    >
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-none border border-white/30 shadow-sm ${colorClass}`}
      >
        <EventIcon type={option.eventType} />
      </span>
      <span className="min-w-0 flex-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] sm:flex-none">
        <span className="block truncate">
          {formatEventTypeLabel(option.eventType)}
        </span>
      </span>
      <span
        className={`ml-1 inline-flex min-w-6 shrink-0 items-center justify-center border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.18em] ${
          isSelected
            ? "border-emerald-200 bg-white/80 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
            : "border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
        }`}
      >
        {option.count}
      </span>
    </button>
  );
};

export const ProviderProgressEventTypeFilter: FC<
  ProviderProgressEventTypeFilterProps
> = ({ options, selectedEventTypes, onIsolate, onReset, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (options.length <= 1) {
    return null;
  }

  const selectedCount = options.filter((option) =>
    selectedEventTypes.has(option.eventType),
  ).length;
  const hasActiveFilter = selectedCount < options.length;
  let summaryLabel = `Showing all ${options.length} event types`;
  if (selectedCount === 0) {
    summaryLabel = "No event types selected";
  } else if (hasActiveFilter) {
    summaryLabel = `Showing ${selectedCount} of ${options.length} event types`;
  }

  return (
    <div className="mb-3 rounded-none border border-slate-200 bg-white/90 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900/70">
      <div className="flex flex-wrap items-start justify-between gap-2 sm:items-center">
        <button
          type="button"
          onClick={() => setIsExpanded((current) => !current)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? "Collapse event filter" : "Expand event filter"
          }
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-none border border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
            <FilterIcon />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
              Event Filter
            </p>
            <p className="truncate text-xs text-slate-600 dark:text-slate-300">
              {summaryLabel}
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 self-start border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 sm:self-auto">
            {selectedCount}/{options.length}
            <ChevronIcon expanded={isExpanded} />
          </span>
        </button>

        {hasActiveFilter && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex min-h-10 w-full items-center justify-center rounded-none border border-slate-200 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 transition-colors hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700 dark:border-slate-700 dark:text-slate-300 dark:hover:border-emerald-500 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300 sm:min-h-0 sm:w-auto sm:justify-start"
          >
            Show all
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="mt-3 grid gap-2 sm:flex sm:flex-wrap">
              {options.map((option) => (
                <EventTypeFilterChip
                  key={option.eventType}
                  option={option}
                  isSelected={selectedEventTypes.has(option.eventType)}
                  onIsolate={() => onIsolate(option.eventType)}
                  onToggle={() => onToggle(option.eventType)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
